import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";

import type { ICountryObject } from "app/services/CountryService";

import { SelectCombobox } from "../SelectCombobox";

import { CountryOptionItem } from "./CountryOptionItem";
import { filterCountryItem } from "./filterCountryItem";

type CountrySelectComboboxProps = {
    items: readonly ICountryObject[];
    selectedIds: readonly number[];
    onSelect: (country: ICountryObject) => void;
    // true: multi-select (checkbox rows, popover stays open on pick). false: single-select.
    multi?: boolean;
    // Intercepts a click on a non-permitted (restricted) country: when it returns true the click is
    // treated as handled (e.g. it opened the hooks/upsell modal) and `onSelect` is NOT invoked.
    // Replaces the legacy `itemWrapper` / CountriesHookWrapper behavior.
    onRestrictedClick?: (country: ICountryObject) => boolean;
    // Override the default flag + name + device-glyph + checkbox row (e.g. a permitted/restricted split).
    renderOption?: (country: ICountryObject, isSelected: boolean) => ReactNode;
    // Emits a separator above the matching option (e.g. before the first restricted country).
    renderSeparatorBefore?: (country: ICountryObject) => boolean;
    renderButton?: () => ReactNode;
    buttonText?: string;
    searchPlaceHolder?: string;
    isTopLayer?: boolean;
    // Opens the popover on mount (mirrors the legacy CountriesDropdown `isOpenedByDefault`).
    defaultOpen?: boolean;
    disabled?: boolean;
    dataAutomation?: string;
};

/**
 * Country-domain wrapper over the non-virtualized {@link SelectCombobox}. Encapsulates the
 * `ICountryObject` item model: flag/name/device-glyph rendering, region-aware search (matches a
 * country by its own name OR a nested state/child name) and non-permitted-country handling via
 * `onRestrictedClick`. Use `multi` to switch between checkbox multi-select and single-select (single
 * closes the popover on pick). Options mount only while the dropdown is open, so a closed dropdown
 * costs ~no DOM — the win over the legacy eager-rendering `CountriesDropdown`.
 */
const CountrySelectCombobox = ({
    items,
    selectedIds,
    onSelect,
    multi = false,
    onRestrictedClick,
    renderOption,
    renderSeparatorBefore,
    renderButton,
    buttonText,
    searchPlaceHolder,
    isTopLayer,
    defaultOpen = false,
    disabled = false,
    dataAutomation,
}: CountrySelectComboboxProps) => {
    const selectedSet = useMemo(() => new Set<number>(selectedIds), [selectedIds]);

    const handleSelect = useCallback(
        (country: ICountryObject) => {
            if (country.permitted === false && onRestrictedClick?.(country)) {
                return;
            }
            onSelect(country);
        },
        [onRestrictedClick, onSelect],
    );

    const renderCountryOption = useCallback(
        (country: ICountryObject, isSelected: boolean) =>
            renderOption ? (
                renderOption(country, isSelected)
            ) : (
                <CountryOptionItem
                    country={country}
                    isSelected={isSelected}
                    multi={multi}
                />
            ),
        [renderOption, multi],
    );

    return (
        <SelectCombobox<ICountryObject>
            items={items}
            isItemSelected={(country) => selectedSet.has(country.id)}
            onSelect={handleSelect}
            selectionMode={multi ? "multiple" : "single"}
            defaultOpen={defaultOpen}
            renderOption={renderCountryOption}
            renderSeparatorBefore={renderSeparatorBefore}
            renderButton={renderButton}
            buttonText={buttonText}
            isTopLayer={isTopLayer}
            hasSearch
            searchPlaceHolder={searchPlaceHolder}
            filterItem={filterCountryItem}
            disabled={disabled}
            dataAutomation={dataAutomation}
        />
    );
};

export default CountrySelectCombobox;
